import React from 'react';
import styles from './header.module.css';
import { usePage } from '../../../context/PageContext';
import { pages } from '../../../context/pages';

/**
 * GiftWrapHeader component displays the header for the gift wrap journey, including title, reset, and close actions.
 * Dynamically renders content based on the current page configuration from PageContext.
 *
 * @param {Object} props - Component props.
 * @param {function} props.onClose - Callback for closing the modal.
 * @returns {import('react').JSX.Element}
 */
export function Header({ onClose }) {
  const { currentPage, goToPage } = usePage();

  // Get the current page configuration.
  const pageConfig = pages[currentPage];

  // Extract content from page configuration.
  const title = pageConfig?.heading || '';
  const description = pageConfig?.description || '';
  const subDescription = pageConfig?.subDescription || '';
  const hasBackButton = pageConfig?.hasBackButton || false;
  const backTo = pageConfig?.backTo || 'firstPage';

  /**
   * Handles the back button click.
   * Navigates to the page defined in the backTo property.
   * @returns {void}
   */
  const handleBackClick = () => {
    goToPage(backTo);
  };

  return (
    <div className={styles['giftWrap__header']}>
      <div className={styles['giftWrap__header-content']}>
        <div className={styles['giftWrap__header-title']}>
          {hasBackButton && (
            <button
              className={styles['giftWrap__header-back'] + ' svg-wrapper button-reset'}
              onClick={handleBackClick}
              aria-label="Go back"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M15.7071 5.29289C16.0976 5.68342 16.0976 6.31658 15.7071 6.70711L10.4142 12L15.7071 17.2929C16.0976 17.6834 16.0976 18.3166 15.7071 18.7071C15.3166 19.0976 14.6834 19.0976 14.2929 18.7071L8.29289 12.7071C7.90237 12.3166 7.90237 11.6834 8.29289 11.2929L14.2929 5.29289C14.6834 4.90237 15.3166 4.90237 15.7071 5.29289Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          )}
          {title && <h2 className="heading-2xl">{title}</h2>}
        </div>
        <div className={styles['giftWrap__header-actions']}>
          {onClose && (
            <button
              className={styles['giftWrap__header-close'] + ' svg-wrapper button-reset'}
              onClick={(e) => onClose(e)}
              aria-label="Close"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M5.35404 4.64645C5.15878 4.45118 4.8422 4.45118 4.64694 4.64645C4.45167 4.84171 4.45167 5.15829 4.64693 5.35355L11.2934 12L4.64693 18.6465C4.45167 18.8417 4.45167 19.1583 4.64694 19.3536C4.8422 19.5488 5.15878 19.5488 5.35404 19.3536L12.0005 12.7071L18.6469 19.3536C18.8422 19.5488 19.1588 19.5488 19.3541 19.3536C19.5493 19.1583 19.5493 18.8417 19.3541 18.6465L12.7076 12L19.3541 5.35355C19.5493 5.15829 19.5493 4.84171 19.3541 4.64645C19.1588 4.45118 18.8422 4.45118 18.6469 4.64645L12.0005 11.2929L5.35404 4.64645Z"
                  fill="#171717"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
      {description && (
        <div className={styles['giftWrap__header-description']}>
          <p className="body-sm">{description}</p>
        </div>
      )}
      {subDescription && (
        <div className={styles['giftWrap__header-sub']}>
          <p className="body-sm">{subDescription}</p>
        </div>
      )}
    </div>
  );
}
